/**
 * The slice of a textarea that `revealTextareaEnd` touches. Keeping it
 * structural lets the unit test drive the helper without a layout engine —
 * jsdom reports every scroll dimension as 0.
 */
export type RevealableTextarea = Pick<
  HTMLTextAreaElement,
  "value" | "scrollHeight" | "setSelectionRange" | "focus"
> & { scrollTop: number };

/**
 * Puts the caret at the end of a textarea and scrolls that end into view.
 *
 * Used when text is dropped into the chat input from the outside — applying a
 * saved prompt from the Prompt Library (LAR-128). A prompt taller than the
 * input's max height otherwise lands scrolled to the top, so the user has to
 * scroll down before they can add anything to it.
 */
export const revealTextareaEnd = (
  textarea: RevealableTextarea | null
): void => {
  if (!textarea) {
    return;
  }

  const end = textarea.value.length;

  // preventScroll: the caret move must not drag the surrounding page around.
  textarea.focus({ preventScroll: true });
  textarea.setSelectionRange(end, end);

  // scrollHeight is the full content height; the browser clamps the assignment
  // to the maximum scroll offset, which is exactly the bottom of the content.
  textarea.scrollTop = textarea.scrollHeight;
};
